import React from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import AvatarBase from 'react-avatar'
const Avatar = AvatarBase as React.ComponentType<{ color?: string; className?: string; name?: string; textSizeRatio?: number; size?: string; round?: boolean }>
import classNames from 'classnames'

import ProductSwitcher from '../../ui/ProductSwitcher'

interface NavChild {
  name: string
  href: string
}

interface NavItem {
  name: string
  href: string
  children?: NavChild[]
}

interface UserNavItem {
  name: string
  href?: string
  onClick?: (e?: React.MouseEvent) => void
}

interface StackedNavbarProps {
  navigation: NavItem[]
  userNavigation: UserNavItem[]
  currentProduct: string
  user: { full_name?: string; entity_name?: string; name?: string; email?: string; allowed_applications?: string[]; [key: string]: unknown }
  isActiveNav: (href: string) => boolean
  headerExtra?: string
  subHeaderOverride?: string
  NotificationMenu?: React.ElementType
  Link: React.ElementType
}

function StackedNavbar({
  navigation,
  userNavigation,
  currentProduct,
  user,
  isActiveNav,
  headerExtra,
  subHeaderOverride,
  NotificationMenu,
  Link,
}: StackedNavbarProps) {
  const mobileNav = navigation.map((item) => (
    <span key={item.name}>
      <Link
        data-cy={`${item.name.replace(/\s+/g, '-').toLowerCase()}-nav`}
        key={item.name}
        to={item.href}
        className={classNames(
          isActiveNav(item.href)
            ? 'bg-perfect-shine-50 border-bonfire-dark text-bonfire-dark'
            : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800',
          'block pl-3 pr-4 py-2 border-l-4 text-base font-medium'
        )}
        aria-current={isActiveNav(item.href) ? 'page' : undefined}
      >
        {item.name}
      </Link>
      {item.children && (
        item.children.map((subItem) => (
          <Link
            data-cy={`${subItem.name.replace(/\s+/g, '-').toLowerCase()}-nav`}
            key={subItem.name}
            to={subItem.href}
            className={classNames(
              isActiveNav(subItem.href)
                ? 'bg-perfect-shine-50 text-bonfire-dark'
                : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800',
              'block pl-8 pr-4 py-2 text-base font-medium'
            )}
            aria-current={isActiveNav(subItem.href) ? 'page' : undefined}
          >
            {subItem.name}
          </Link>
        ))
      )}
    </span>
  ))

  const mainNav = navigation.map((item) => (
    <div
      style={{ borderBottomWidth: '3px' }}
      className={classNames(
        isActiveNav(item.href)
          ? 'border-bonfire-dark text-bonfire-dark-light'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
        'relative inline-flex items-center px-1 pt-1 text-sm font-medium',
      )}
      key={item.name}>
      <Link
        data-cy={`${item.name.replace(/\s+/g, '-').toLowerCase()}-nav`}
        key={item.name}
        to={item.href}
        className="flex h-full items-center w-full"
        aria-current={isActiveNav(item.href) ? 'page' : undefined}
      >
        {item.name}
      </Link>
      {isActiveNav(item.href) && item.children && (
        <div
          className="absolute bottom-0 left-0 h-16 inline-flex items-center text-sm font-medium space-x-4 divide-x divide-gray-300"
          style={{ transform: 'translate(-30%, 100%)' }}>
          {item.children.map(subItem => (
            <div
              data-cy={`${subItem.name.replace(/\s+/g, '-').toLowerCase()}-subnav`}
              key={subItem.name}
              className={classNames(
                isActiveNav(subItem.href)
                  ? 'text-bonfire-dark-light'
                  : 'text-gray-500',
                'w-max pl-4',
              )}
            >
              <Link to={subItem.href}>
                {subItem.name}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  ))

  const displayName = String(user.full_name ?? '')
  const displaySubHeader = subHeaderOverride ?? String(user.entity_name ?? '')

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <>
          <div className="bg-white border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between h-16">
              <div className="flex">
                <div className="shrink-0 flex items-center">
                  <ProductSwitcher currentUser={user} currentProduct={currentProduct}
                    climateApps={window.config?.climateApps as unknown as Record<string, string>} />
                </div>
                <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                  {mainNav}
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex items-center">

                  {NotificationMenu && <NotificationMenu />}

                  {/* Profile dropdown */}
                  <Menu as="div" className="hidden sm:block ml-3 relative">
                    {({ open: menuOpen }) => (
                      <>
                        <div data-cy="profile-button">
                          <MenuButton
                            className="bg-white flex text-sm rounded-full focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-perfect-shine">
                            <Avatar color="transparent" className="bg-pacific-storm" name={user.full_name}
                              textSizeRatio={2} size="40" round={true} />
                          </MenuButton>
                        </div>
                        <Transition
                          show={menuOpen}
                          enter="transition ease-out duration-200"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <MenuItems
                            static
                            className="z-50 origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black/5 focus:outline-hidden"
                          >
                            <MenuItem as="span"
                              className="truncate cursor-default text-white rounded-t-md bg-pacific-storm block px-4 pt-2 text-xs">
                              {displayName}{headerExtra ? ` (${headerExtra})` : ''}
                            </MenuItem>
                            <MenuItem as="span"
                              className="truncate cursor-default text-white bg-pacific-storm block px-4 pb-2 text-xs">
                              {displaySubHeader}
                            </MenuItem>
                            {userNavigation.map((item) => (
                              <MenuItem
                                key={item.name}>
                                {({ active }) => (
                                  <a
                                    data-cy={`${item.name.replace(/\s+/g, '-').toLowerCase()}-menu-item`}
                                    href={item.href}
                                    onClick={item.onClick}
                                    className={classNames(
                                      active ? 'bg-gray-100' : '',
                                      'block px-4 py-2 text-sm text-gray-700 cursor-pointer'
                                    )}
                                  >
                                    {item.name}
                                  </a>
                                )}
                              </MenuItem>
                            ))}
                          </MenuItems>
                        </Transition>
                      </>
                    )}
                  </Menu>
                </div>
                <div className="pl-3 flex items-center sm:hidden">
                  {/* Mobile menu button */}
                  <DisclosureButton
                    className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-perfect-shine">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </DisclosureButton>
                </div>
              </div>

            </div>
          </div>
          <div className="hidden sm:flex h-16">
            {/* subnav height holder */}
          </div>


          <DisclosurePanel className="sm:hidden">
            <div className="pt-2 pb-3">
              {mobileNav}
            </div>
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4">
                <div className="shrink-0">
                  <Avatar color="transparent" className="bg-pacific-storm focus:ring-perfect-shine"
                    name={user.full_name} textSizeRatio={2} size={'40'} round={true} />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">{String(user.name ?? '')}</div>
                  <div className="text-sm font-medium text-gray-500">{String(user.email ?? '')}</div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {userNavigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={item.onClick}
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 cursor-pointer"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}

export default StackedNavbar
export type { NavItem, NavChild, UserNavItem, StackedNavbarProps }
